'use client'

import cookie from 'js-cookie'
import { jwtDecode } from 'jwt-decode'
import Router from 'next/router'
import { isHealthCompanionEnabled } from '@/lib/Ameli/Core/healthCompanionFlag'
import { getDomainSetting } from '@/lib/getDomainSettings'
import { markPharmUppSsoSession } from '@/lib/pharmUpp/pharmUppSession'

export enum StatusSession {
  AUTHENTICATED = 'authenticated',
  LOADING = 'loading',
  UNAUTHENTICATED = 'unauthenticated',
}

export const LOCAL_REDIRECT_AFTER_LOGIN = 'requestedUrl'

// Canal entre onglets du même navigateur : prévient les autres onglets qu'un changement de
// compte vient d'avoir lieu (bascule d'un poste Care sur le compte comptoir).
const ACCOUNT_CHANNEL = 'pharmedigroup-auth'
const ACCOUNT_SWITCHED_MESSAGE = 'account-switched'

// Clés de sessionStorage propres au compte connecté, à purger quand on change de compte.
// `pharmedigroup-totp-unlocked` : même clé que TOTP_UNLOCK_KEY, dupliquée pour garder ce
// module sans dépendance UI (cf. logout dans session.ts).
const ACCOUNT_SCOPED_STORAGE_KEYS = [
  'pharmedigroup-accessToken',
  'pharmedigroup-switchUser',
  'pharmedigroup-switchUserRoles',
  'pharmedigroup-careActorToken',
  'pharmedigroup-careActor',
  'pharmedigroup-proSanteConnectSessionId',
  'pharmedigroup-totp-unlocked',
]

const clearAccountScopedStorage = () => {
  if (typeof sessionStorage === 'undefined') return

  ACCOUNT_SCOPED_STORAGE_KEYS.forEach(key => sessionStorage.removeItem(key))
}

export type CareActor = {
  id: number
  firstname: string | null
  lastname: string | null
}

export type Session = {
  user: {
    id: string
    email: string
    name: string
    roles: string[]
  } | null
  expiresAt: number
  status: StatusSession
  accessToken: string | null
  refreshToken: string | null
  switchUser: string | null
  switchUserRoles: string[] | null
  /** Short-lived token identifying the team member acting behind the counter (PIN). */
  careActorToken: string | null
  careActor: CareActor | null
  proSanteConnectSessionId: string | null
}

type LoginProvider =
  | 'pharmedigroup'
  | 'mobile'
  | 'google'
  | 'ssoGroup'
  | 'ssoGroupPharmUpp'
  | 'proSanteConnect'
  | 'proSanteConnectLink'

type LoginArgs = {
  username?: string
  password?: string
  entity?: string
  userAgent?: string
  fcmToken?: string
  callbackUrl?: string
  redirect?: boolean
  language?: string
  token?: string
  registrationTicket?: string
  refreshToken?: string
  sessionState?: string
  redirectUri?: string
  email?: string
  sub?: string
  /** Code OTP a 6 chiffres saisi apres le mot de passe (users industry + admins sans company). */
  oneTimePassword?: string
}

/**
 * Resultat de signIn :
 *  - { otpRequired: true, email? , otpError? } : le mot de passe est bon mais un code OTP
 *    email est exige (aucun token stocke, aucune redirection). email masque a l'etape 1,
 *    otpError='invalid' si le code saisi est errone.
 *  - { otpRequired: false } : connexion aboutie (tokens stockes + redirection).
 */
export type SignInResult =
  | { otpRequired: true; email?: string; otpError?: 'invalid' }
  | { otpRequired: false }

export const INITIAL_SESSION: Session = {
  user: null,
  expiresAt: 0,
  status: StatusSession.LOADING,
  accessToken: null,
  refreshToken: null,
  switchUser: null,
  switchUserRoles: null,
  careActorToken: null,
  careActor: null,
  proSanteConnectSessionId: null,
}

const getValueFromStorage = (key: string) => {
  if (typeof sessionStorage !== 'undefined') {
    return sessionStorage.getItem(key)
  }

  return null
}

const setValueToStorage = (key: string, value: string) => {
  if (typeof sessionStorage !== 'undefined') {
    sessionStorage.setItem(key, value)
  }
}

class AuthClient {
  private session: Session = { ...INITIAL_SESSION }
  private sessionChangeCallback: ((session: Session) => void) | null = null
  private readonly baseUrl: string
  private proSanteConnectRefreshTimer: ReturnType<typeof setInterval> | null = null
  private lastActivityAt = Date.now()

  constructor() {
    this.baseUrl = getDomainSetting('urlApiGroup')

    const accessToken = getValueFromStorage('pharmedigroup-accessToken')
    const refreshToken = cookie.get('pharmedigroup-refreshToken') || null

    const switchUser = getValueFromStorage('pharmedigroup-switchUser')
    const switchUserRoles = getValueFromStorage('pharmedigroup-switchUserRoles')

    const careActorToken = getValueFromStorage('pharmedigroup-careActorToken')
    const careActor = getValueFromStorage('pharmedigroup-careActor')
    const proSanteConnectSessionId = getValueFromStorage('pharmedigroup-proSanteConnectSessionId')

    this.session.accessToken = accessToken === 'null' ? null : accessToken
    this.session.refreshToken = refreshToken === 'null' ? null : refreshToken
    this.session.switchUser = switchUser === 'null' ? null : switchUser
    this.session.switchUserRoles = switchUserRoles === 'null' ? null : JSON.parse(switchUserRoles)
    this.session.careActorToken = careActorToken === 'null' ? null : careActorToken
    this.session.careActor =
      careActor === 'null' || !careActor ? null : (JSON.parse(careActor) as CareActor)
    this.session.proSanteConnectSessionId =
      proSanteConnectSessionId === 'null' ? null : proSanteConnectSessionId

    // Un autre onglet a changé de compte : le cookie de refresh est partagé, mais l'accessToken
    // de cet onglet (sessionStorage, valable 24 h) est encore celui de l'ancien compte. On le
    // jette et on recharge : l'onglet repart du cookie, donc du nouveau compte.
    if (typeof window !== 'undefined' && 'BroadcastChannel' in window) {
      new BroadcastChannel(ACCOUNT_CHANNEL).onmessage = event => {
        if (event.data?.type !== ACCOUNT_SWITCHED_MESSAGE) return

        this.session = { ...INITIAL_SESSION }
        clearAccountScopedStorage()
        window.location.reload()
      }
    }

    if (typeof window !== 'undefined') {
      const recordActivity = () => {
        this.lastActivityAt = Date.now()
      }
      ;['click', 'keydown', 'touchstart'].forEach(eventName =>
        window.addEventListener(eventName, recordActivity, { passive: true })
      )
    }

    if (this.session.refreshToken) {
      // Fire-and-forget : updateSession() gère déjà le signOut/redirect en interne.
      // Sans le catch, un refresh en échec (401) part en unhandledrejection (Sentry).
      this.updateSession().catch(() => {})

      const twelveHours = 12 * 60 * 60 * 1000

      setInterval(() => {
        this.updateSession().catch(() => {})
      }, twelveHours)
    } else this.overrideSession({ status: StatusSession.UNAUTHENTICATED })

    this.startProSanteConnectRefresh()
  }

  async signIn(provider: LoginProvider, credentials: LoginArgs): Promise<SignInResult> {
    this.overrideSession({
      status: StatusSession.LOADING,
    })

    try {
      let tokenDecode = null
      let result = null

      if (
        provider === 'pharmedigroup' ||
        provider === 'ssoGroup' ||
        provider === 'ssoGroupPharmUpp' ||
        provider === 'google' ||
        provider === 'proSanteConnect' ||
        provider === 'proSanteConnectLink'
      ) {
        result = await this._loginOnServer(provider, credentials)

        // Etape OTP : mot de passe valide mais code email exige. On ne stocke aucun token
        // et on ne redirige pas ; l'appelant (FormLogin) affiche l'ecran de saisie du code.
        if (result?.otpRequired) {
          this.overrideSession({ status: StatusSession.UNAUTHENTICATED })

          return { otpRequired: true, email: result.email, otpError: result.otpError }
        }

        if (!result?.token) {
          this.overrideSession({ status: StatusSession.UNAUTHENTICATED })

          return result
        }

        tokenDecode = jwtDecode(result.token)
      } else if (provider === 'mobile') {
        result = {
          token: credentials.token,
          refresh_token: credentials.refreshToken,
        }

        tokenDecode = jwtDecode(credentials.token)
      }

      sessionStorage.setItem('pharmedigroup-accessToken', result?.token)
      cookie.set('pharmedigroup-refreshToken', result?.refresh_token, { expires: 30 })
      // Bandeau marque grise PharmUPP : posé par ce SSO, retiré par toute autre connexion.
      markPharmUppSsoSession(provider === 'ssoGroupPharmUpp')

      this.overrideSession({
        user: {
          id: String(tokenDecode?.id),
          email: tokenDecode?.email,
          name: tokenDecode?.email,
          roles: tokenDecode?.roles,
        },
        expiresAt: tokenDecode?.exp,
        status: StatusSession.AUTHENTICATED,
        accessToken: result?.token,
        refreshToken: result?.refresh_token,
        switchUser: null,
        switchUserRoles: null,
        proSanteConnectSessionId: result?.proSanteConnectSessionId ?? null,
      })

      this.startProSanteConnectRefresh()
      window.companion?.connect(result?.token, result?.refresh_token)

      if (credentials.callbackUrl) Router.replace(credentials.callbackUrl)

      return { otpRequired: false }
    } catch (error) {
      console.error('Error when login: ', error)

      this.overrideSession({
        status: StatusSession.UNAUTHENTICATED,
      })

      throw error instanceof Error
        ? error
        : new Error('Impossible de vous identifier avec ces identifiants')
    }
  }

  /**
   * Remplace la session par celle d'un autre compte, dont les jetons viennent d'être émis
   * par l'API (bascule d'un poste Care sur le compte comptoir).
   *
   * Ce n'est PAS un switch-user : la session précédente est abandonnée (son refresh token est
   * révoqué côté serveur par l'endpoint qui a émis ces jetons), sans retour possible. Rien ne
   * doit en survivre : ni switch-user, ni acteur comptoir, ni session PSC, ni coffre TOTP
   * déverrouillé. Le rechargement complet final vide aussi le cache TanStack, dont les clés
   * ne portent pas l'identité : une navigation client servirait le profil précédent.
   */
  switchAccount(tokens: { token: string; refreshToken: string }, redirectUrl: string) {
    const tokenDecode: any = jwtDecode(tokens.token)

    this.stopProSanteConnectRefresh()
    // Mise à jour en mémoire sans prévenir React : la page est rechargée juste après. Un
    // overrideSession() déclenché d'ici là réécrirait donc les jetons du NOUVEAU compte.
    this.session = {
      ...INITIAL_SESSION,
      user: {
        id: String(tokenDecode?.id),
        email: tokenDecode?.email,
        name: tokenDecode?.email,
        roles: tokenDecode?.roles,
      },
      expiresAt: tokenDecode?.exp,
      status: StatusSession.AUTHENTICATED,
      accessToken: tokens.token,
      refreshToken: tokens.refreshToken,
    }

    clearAccountScopedStorage()
    markPharmUppSsoSession(false)
    sessionStorage.setItem('pharmedigroup-accessToken', tokens.token)
    cookie.set('pharmedigroup-refreshToken', tokens.refreshToken, { expires: 30 })

    // Le poste Companion suit le compte du poste.
    window.companion?.connect(tokens.token, tokens.refreshToken)

    if ('BroadcastChannel' in window) {
      const channel = new BroadcastChannel(ACCOUNT_CHANNEL)
      channel.postMessage({ type: ACCOUNT_SWITCHED_MESSAGE })
      channel.close()
    }

    window.location.href = redirectUrl
  }

  async signOut(options: {
    redirect: boolean
    callbackUrl: string
    propagateToProSanteConnect?: boolean
  }) {
    let redirectUrl = options?.callbackUrl

    if (
      options.propagateToProSanteConnect !== false &&
      this.session.proSanteConnectSessionId &&
      this.session.accessToken
    ) {
      try {
        const response = await fetch(`${this.baseUrl}pro-sante-connect/logout-url`, {
          method: 'POST',
          body: JSON.stringify({
            proSanteConnectSessionId: this.session.proSanteConnectSessionId,
            postLogoutRedirectUri: `${window.location.origin}/login?logout=pro-sante-connect`,
            localRefreshToken: this.session.refreshToken,
          }),
          headers: {
            Authorization: `Bearer ${this.session.accessToken}`,
            'Content-Type': 'application/json',
          },
        })
        const data = await response.json().catch(() => null)
        if (response.ok && data?.logoutUrl) redirectUrl = data.logoutUrl
      } catch {
        // Un echec PSC ne doit jamais empecher la deconnexion locale.
      }
    }

    // Révocation serveur AVANT d'effacer l'état local : sans elle, la ligne
    // `refresh_tokens` survit à la déconnexion et une copie du token continue
    // d'ouvrir des sessions. Depuis le passage à `ttl_update: true` côté API,
    // cette ligne ne s'éteint plus d'elle-même tant qu'elle sert.
    //
    // ATTENTION : un accessToken expiré ne rend PAS le refresh token inoffensif — il
    // reste utilisable 30 jours après son dernier usage. Si l'invalidation prend un 401
    // (utilisateur inactif >1h qui clique Déconnexion), on obtient un jeton d'accès
    // frais via /token/refresh — SANS toucher à l'état de session, qu'on est en train
    // de détruire — uniquement pour porter la révocation, puis on retente une fois.
    // Un échec ne doit jamais empêcher la déconnexion locale, même règle que PSC.
    if (this.session.refreshToken) {
      const refreshToken = this.session.refreshToken
      const invalidate = (accessToken: string) =>
        fetch(`${this.baseUrl}token/invalidate`, {
          method: 'POST',
          body: JSON.stringify({ refreshToken }),
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
        })
      try {
        const response = this.session.accessToken
          ? await invalidate(this.session.accessToken)
          : null
        if (!response || response.status === 401) {
          const refreshed = await fetch(`${this.baseUrl}token/refresh`, {
            method: 'POST',
            body: JSON.stringify({ refresh_token: refreshToken }),
            headers: { 'Content-Type': 'application/json' },
          })
          const data = await refreshed.json().catch(() => null)
          if (refreshed.ok && data?.token) await invalidate(data.token)
        }
      } catch {
        // Réseau coupé ou API injoignable : on déconnecte localement quand même.
      }
    }

    window.companion?.disconnect()
    // Companion santé : on lui reprend le JWT confié pour Care (carte n° 158). Import dynamique
    // pour ne pas charger le transport companion dans le shell ; un échec (companion absent, non
    // appairé) ne doit jamais retenir la déconnexion. Interrupteur coupé : rien à reprendre.
    if (isHealthCompanionEnabled()) {
      try {
        const { closeHealthCompanionCareSession } = await import(
          '@/lib/hook/companion/healthCompanionCare'
        )
        await closeHealthCompanionCareSession()
      } catch {
        // Companion santé injoignable : rien à reprendre.
      }
    }
    sessionStorage.removeItem('pharmedigroup-accessToken')
    sessionStorage.removeItem('pharmedigroup-proSanteConnectSessionId')
    await cookie.remove('pharmedigroup-refreshToken')
    markPharmUppSsoSession(false)
    this.stopProSanteConnectRefresh()

    this.session = {
      ...INITIAL_SESSION,
      status: StatusSession.UNAUTHENTICATED,
    }
    this.sessionChangeCallback?.(this.session)

    if (options?.redirect && redirectUrl) window.location.href = redirectUrl
  }

  async updateSession() {
    try {
      // On relit le cookie (partagé entre onglets) plutôt que la copie en mémoire, pour
      // utiliser la version la plus fraîche : un onglet resté ouvert doit repartir du
      // token courant et non de celui qu'il détenait à son ouverture.
      //
      // Le refresh token n'est PAS roté aujourd'hui (`single_use: false` côté API, cf.
      // config/packages/gesdinet_jwt_refresh_token.yaml) : le serveur renvoie le même.
      // Sa validité, elle, est repoussée à chaque refresh (`ttl_update: true`), d'où une
      // session glissante tant que l'utilisateur revient dans les 30 jours.
      const cookieToken = cookie.get('pharmedigroup-refreshToken')
      const refreshToken =
        cookieToken && cookieToken !== 'null' ? cookieToken : this.session.refreshToken

      if (!refreshToken) throw new Error('No refresh token')

      this.overrideSession({
        status: StatusSession.LOADING,
      })

      const response = await fetch(`${this.baseUrl}token/refresh`, {
        method: 'POST',
        body: JSON.stringify({ refresh_token: refreshToken }),
        headers: {
          'Content-Type': 'application/json',
        },
      })

      if (!response.ok) {
        const error = new Error(`Token refresh failed (${response.status})`)
        ;(error as Error & { status?: number }).status = response.status
        throw error
      }

      const refetchToken = await response.json()

      if (!refetchToken?.token) {
        throw new Error('[AuthClient:session/refresh] Jeton manquant dans la réponse')
      }

      if (!refetchToken?.refresh_token) {
        throw new Error('[AuthClient:session/refresh] Refresh token manquant dans la réponse')
      }

      const tokenDecode: any = jwtDecode(refetchToken.token)
      if (!tokenDecode?.email) {
        throw new Error('decoded token missing required fields')
      }

      this.overrideSession({
        user: {
          id: String(tokenDecode.id),
          email: tokenDecode.email,
          name: tokenDecode.email,
          roles: tokenDecode.roles,
        },
        expiresAt: tokenDecode.exp,
        status: StatusSession.AUTHENTICATED,
        accessToken: refetchToken.token,
        refreshToken: refetchToken.refresh_token,
        switchUser: this.session.switchUser,
        switchUserRoles: this.session.switchUserRoles,
      })

      window.companion?.connect(refetchToken.token, refetchToken.refresh_token)
    } catch (error) {
      console.error('Error when update session: ', error)

      // 401 = refresh token invalide/expiré/déjà roté : la session est irrécupérable.
      // Pour les autres erreurs (réseau, 5xx), on ne déconnecte que si l'accessToken
      // est réellement expiré (exp JWT en secondes, Date.now() en millisecondes).
      const isUnauthorized = (error as Error & { status?: number })?.status === 401
      const expiresAtMs = (this.session?.expiresAt ?? 0) * 1000

      if (isUnauthorized || expiresAtMs < Date.now()) {
        const pathname = typeof window !== 'undefined' ? window.location.pathname : ''
        const alreadyOnLogin = pathname.startsWith('/login')
        const isPublicPage =
          pathname.startsWith('/register') ||
          pathname.startsWith('/inscription') ||
          pathname.startsWith('/public/') ||
          pathname === '/contact'

        this.signOut({
          redirect: !alreadyOnLogin && !isPublicPage,
          callbackUrl: '/login',
          propagateToProSanteConnect: false,
        })

        if (alreadyOnLogin) {
          // Pas de reload /login -> /login : on nettoie juste l'état local.
          this.overrideSession({
            status: StatusSession.UNAUTHENTICATED,
            accessToken: null,
            refreshToken: null,
            switchUser: null,
            switchUserRoles: null,
          })
        }
      } else {
        // Erreur transitoire (réseau, 5xx) avec un accessToken encore valide : on sort du
        // statut LOADING posé avant le fetch pour ne pas bloquer l'UI sur un spinner.
        this.overrideSession({ status: StatusSession.AUTHENTICATED })
      }

      throw error
    }
  }

  overrideRolesSession(roles: string[] | null | undefined) {
    const nextRoles = Array.isArray(roles) ? roles : []

    if (nextRoles.length === 0) {
      return
    }
    if (this.session.switchUserRoles?.length > 0) {
      if (!this.areRolesDifferent(this.session.switchUserRoles, nextRoles)) {
        return
      }
      this.overrideSession({
        switchUserRoles: nextRoles,
      })
    } else {
      if (!this.session.user || !this.areRolesDifferent(this.session.user.roles, nextRoles)) {
        return
      }
      //si on n'est pas en switchUser on met à jour le role de l'utilisateur'
      this.overrideSession({
        user: {
          ...this.session.user,
          roles: nextRoles,
        },
      })
    }
  }

  overrideSession(session: Partial<Session>) {
    this.session = {
      ...this.session,
      ...session,
      user: {
        ...this.session.user,
        ...session.user,
      },
    }

    if (this.session.refreshToken) {
      cookie.set('pharmedigroup-refreshToken', this.session.refreshToken, { expires: 30 })
    }
    setValueToStorage('pharmedigroup-accessToken', this.session.accessToken)
    setValueToStorage('pharmedigroup-switchUser', this.session.switchUser)
    setValueToStorage('pharmedigroup-switchUserRoles', JSON.stringify(this.session.switchUserRoles))
    setValueToStorage('pharmedigroup-careActorToken', this.session.careActorToken)
    setValueToStorage('pharmedigroup-careActor', JSON.stringify(this.session.careActor))
    setValueToStorage(
      'pharmedigroup-proSanteConnectSessionId',
      this.session.proSanteConnectSessionId
    )

    this.sessionChangeCallback?.(this.session)
  }

  getSession(): Session {
    return this.session
  }

  registerSessionChangeCallback(callback: (session: Session) => void) {
    if (!callback) return

    this.sessionChangeCallback = callback

    callback(this.session)
  }

  /**
   * Appelle l'endpoint REST correspondant au provider et retourne { token, refresh_token }
   */
  private async _loginOnServer(
    provider: LoginProvider,
    credentials: LoginArgs
  ): Promise<{
    token?: string
    refresh_token?: string
    otpRequired?: boolean
    email?: string
    otpError?: 'invalid'
    // Session Pro Sante Connect (develop) : relue a la construction de la session.
    proSanteConnectSessionId?: string
  }> {
    let endpoint: string
    let body: Record<string, unknown>
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    }

    if (provider === 'pharmedigroup') {
      endpoint = 'authenticate'
      body = {
        username: credentials.username,
        password: credentials.password,
        entity: credentials.entity,
        userAgent: credentials.userAgent,
        fcmToken: credentials.fcmToken,
        // Present uniquement a l'etape 2 (saisie du code) — sinon undefined (ignore au JSON).
        oneTimePassword: credentials.oneTimePassword,
      }
      if (credentials.language) {
        headers['Accept-Language'] = credentials.language
      }
    } else if (provider === 'ssoGroup') {
      endpoint = 'sso_group_check'
      body = { token: credentials.token }
    } else if (provider === 'ssoGroupPharmUpp') {
      // SSO PharmUpp goes through server-side API route to protect the API token
      const response = await fetch('/api/sso-pharm-upp', {
        method: 'POST',
        body: JSON.stringify({
          token: credentials.token,
          sessionState: credentials.sessionState,
          redirectUri: credentials.redirectUri,
        }),
        headers: { 'Content-Type': 'application/json' },
      })

      if (!response.ok) {
        const errorText = await response.text().catch(() => 'Erreur serveur')
        throw new Error(errorText || `Erreur ${response.status}`)
      }

      const result = await response.json()
      if (!result?.token) {
        throw new Error('Jeton manquant dans la réponse')
      }

      return result
    } else if (provider === 'google') {
      endpoint = 'google_authenticate'
      body = {
        idToken: credentials.token,
        email: credentials.email,
        googleId: credentials.sub,
      }
    } else if (provider === 'proSanteConnect') {
      endpoint = 'pro-sante-connect/exchange'
      body = { ticket: credentials.token }
    } else if (provider === 'proSanteConnectLink') {
      endpoint = 'pro-sante-connect/link'
      body = {
        registrationTicket: credentials.registrationTicket,
        email: credentials.username,
        password: credentials.password,
      }
    } else {
      throw new Error(`Provider "${provider}" non supporté pour _loginOnServer`)
    }

    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      method: 'POST',
      body: JSON.stringify(body),
      headers,
    })

    if (!response.ok) {
      const errorText = await response.text().catch(() => 'Erreur serveur')
      // L'API renvoie souvent un corps JSON { error | message: "..." } (ex: "Utilisateur non trouvé").
      // On extrait le message lisible plutôt que de propager la chaîne JSON brute dans l'Error.
      let errorMessage = errorText
      try {
        const parsed = JSON.parse(errorText)
        errorMessage = parsed?.error || parsed?.message || errorText
      } catch {
        // errorText n'est pas du JSON : on le garde tel quel.
      }
      throw new Error(errorMessage || `Erreur ${response.status}`)
    }

    const result = await response.json()

    // Reponse intermediaire OTP (mot de passe OK, code email exige) : pas de token attendu.
    if (result?.otpRequired) {
      return result
    }

    if (!result?.token && provider !== 'proSanteConnect') {
      throw new Error('Jeton manquant dans la réponse')
    }

    return result
  }

  private startProSanteConnectRefresh() {
    if (!this.session.proSanteConnectSessionId || this.proSanteConnectRefreshTimer) return

    this.proSanteConnectRefreshTimer = setInterval(() => {
      const recentlyActive = Date.now() - this.lastActivityAt < 2 * 60 * 1000
      const pageVisible = typeof document === 'undefined' || document.visibilityState === 'visible'
      if (!recentlyActive || !pageVisible) return

      this.refreshProSanteConnectSession()
        .then(status => {
          // Ne deconnecter que si la session PSC est reellement invalide/expiree.
          // Une erreur reseau ou une 5xx transitoire ne doit pas ejecter l'utilisateur :
          // on retentera au prochain tick.
          if (status === 'invalid') {
            this.signOut({
              redirect: true,
              callbackUrl: '/login?error=pro-sante-connect-session-expired',
              propagateToProSanteConnect: false,
            })
          }
        })
        .catch(() => {})
    }, 90 * 1000)
  }

  private stopProSanteConnectRefresh() {
    if (!this.proSanteConnectRefreshTimer) return

    clearInterval(this.proSanteConnectRefreshTimer)
    this.proSanteConnectRefreshTimer = null
  }

  private async refreshProSanteConnectSession(): Promise<'ok' | 'invalid' | 'retry'> {
    if (!this.session.proSanteConnectSessionId || !this.session.accessToken) return 'retry'

    const response = await fetch(`${this.baseUrl}pro-sante-connect/refresh`, {
      method: 'POST',
      body: JSON.stringify({
        proSanteConnectSessionId: this.session.proSanteConnectSessionId,
      }),
      headers: {
        Authorization: `Bearer ${this.session.accessToken}`,
        'Content-Type': 'application/json',
      },
    })

    if (response.ok) return 'ok'

    // 400 : refresh token PSC absent ou expire. 401 : session PSC invalide... ou simple JWT
    // Pharmedigroup expire. Dans ce dernier cas on retente (updateSession() renouvelle le JWT),
    // sinon on signale la session PSC comme morte.
    if (response.status === 400) return 'invalid'
    if (response.status === 401) {
      const jwtExpired = !!this.session.expiresAt && this.session.expiresAt * 1000 < Date.now()

      return jwtExpired ? 'retry' : 'invalid'
    }

    return 'retry'
  }

  private areRolesDifferent(
    currentRoles: string[] | null | undefined,
    nextRoles: string[]
  ): boolean {
    const normalize = (roles: string[] | null | undefined) =>
      Array.from(new Set(roles ?? [])).sort()

    const normalizedCurrent = normalize(currentRoles)
    const normalizedNext = normalize(nextRoles)

    if (normalizedCurrent.length !== normalizedNext.length) {
      return true
    }

    return normalizedCurrent.some((role, index) => role !== normalizedNext[index])
  }
}

export default AuthClient
